// src/components/LoadDetails.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import { FaCalendarAlt, FaClock } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import "../styles/LoadDetails.css";

// 💰 money helpers
function formatMoney(amount, currency = "EUR") {
  if (amount == null || amount === "") return "";
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  try {
    return new Intl.NumberFormat("pt-PT", { style: "currency", currency }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

function extractPrice(obj = {}) {
  // Try a few likely nests just in case:
  const price         = obj.price ?? obj.merchandise?.price ?? obj.pricing?.price ?? null;
  const priceCurrency = obj.priceCurrency ?? obj.merchandise?.priceCurrency ?? obj.pricing?.currency ?? "EUR";
  return { price, priceCurrency };
}

// 🆕 Notes for driver (support several possible nests/keys)
function extractNotes4Driver(obj = {}) {
  return (
    obj.notes4driver ??
    obj.notesForDriver ??
    obj.notes_driver ??
    obj.merchandise?.notes4driver ??
    obj.pricing?.notes4driver ??
    obj.notes ?? // fallback if legacy used 'notes'
    ""
  );
}
/* ==== END helpers ==== */

/* ==== Working Hours helpers ==== */
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function weekdayKeyFromISO(isoDateStr) {
  if (!isoDateStr) return null;
  const d = new Date(isoDateStr);
  if (Number.isNaN(d)) return null;
  return DAYS[d.getDay()];
}
function parseWorkingHours(wh) {
  if (!wh) return null;
  if (typeof wh === "string") {
    try { return JSON.parse(wh); } catch { return null; }
  }
  if (typeof wh === "object") return wh;
  return null;
}
function formatIntervalsForDay(workingHours, dayKey) {
  if (!workingHours || !dayKey) return null;
  const day = workingHours[dayKey];
  if (!Array.isArray(day)) return null;
  if (day.length === 0) return "Closed";
  return day.map(([start, end]) => `${start}–${end}`).join(", ");
}
function getSessionCounter(obj = {}) {
  return (
    obj?.transportSession?.transportSessionNumber ??
    obj?.tsUiCounter ??
    obj?.transportsessionNumber ??
    null
  );
}
/* ==== END helpers ==== */

const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/* ==== Normalize helpers (pickupDeliveries -> pickupPoints/deliveryPoints) ==== */
function pdToPoint(pd = {}) {
  const place = pd.place || {};
  return {
    location: pd.location ?? place.placeName ?? "",
    date: pd.date ?? pd.datetime ?? null,
    hour: pd.hour ?? "",
    address: pd.address ?? place.address ?? "",
    postalCode: pd.postalCode ?? place.postalCode ?? "",
    city: pd.city ?? place.city ?? "",
    country: pd.country ?? place.country ?? "",
    gMapsLink: pd.gMapsLink ?? place.gMapsLink ?? "",
    coordinates: pd.coordinates ?? place.coordinates ?? "",
    contactPhone: pd.contactPhone ?? place.contactPhone ?? "",
    workingHours: pd.workingHours ?? place.workingHours ?? null,
  };
}
function buildPointsFromPDs(pickupDeliveries = [], type) {
  return (pickupDeliveries || [])
    .filter(pd => String(pd?.type).toUpperCase() === type)
    .map(pdToPoint);
}

/* ==== Dimensions helpers ==== */
function pickDefined(...vals) {
  for (const v of vals) if (v !== undefined && v !== null && v !== "") return v;
  return undefined;
}

function extractDims(obj = {}) {
  return {
    length: pickDefined(
      obj.length,
      obj.totalLength,
      obj.dimensions?.length,
      obj.size?.length,
      obj.merchandise?.length
    ),
    height: pickDefined(
      obj.height,
      obj.dimensions?.height,
      obj.size?.height,
      obj.merchandise?.height
    ),
    width: pickDefined(
      obj.width,
      obj.dimensions?.width,
      obj.size?.width,
      obj.merchandise?.width
    ),
  };
}

function withNormalizedPoints(obj = {}) {
  const fromPDsPickup   = buildPointsFromPDs(obj.pickupDeliveries, "PICKUP");
  const fromPDsDelivery = buildPointsFromPDs(obj.pickupDeliveries, "DELIVERY");

  // Preferir SEMPRE o que vem de pickupDeliveries (pois traz os campos completos do place)
  const pickupPoints = fromPDsPickup.length
    ? fromPDsPickup
    : (Array.isArray(obj.pickupPoints) ? obj.pickupPoints : []);

  const deliveryPoints = fromPDsDelivery.length
    ? fromPDsDelivery
    : (Array.isArray(obj.deliveryPoints) ? obj.deliveryPoints : []);

  return { ...obj, pickupPoints, deliveryPoints };
}

/* Small UI helper */
function valueOrEmpty(v) {
  return v === undefined || v === null ? "" : String(v);
}

function computeClientType(obj = {}) {
  if (obj?.clientType) return obj.clientType;
  if (obj?.industryClientId) return 'industry';
  if (obj?.carrierClientId)  return 'carrier';
  return '';
}
/* ==== END helpers ==== */

export default function LoadDetails() {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const prefill = state?.prefill ?? null;

  // 1) Load base object (pontos, clientes, etc.)
  const [load, setLoad] = useState(prefill ? withNormalizedPoints(prefill) : null);
  const [error, setError] = useState("");

  // 2) Dimensões em estado próprio para não “piscar”
  const prefillDims = extractDims(prefill || {});
  const [dims, setDims] = useState(prefillDims);

  // “Mostrar mais” por índice
  const [showMorePickupFields, setShowMorePickupFields] = useState({});
  const [showMoreDeliveryFields, setShowMoreDeliveryFields] = useState({});

  // 3) Hidratar em background
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/loads/${loadId}/test-light`);
        if (cancelled) return;

        // merge do objeto principal (pontos, labels, etc.)
        setLoad(prev => withNormalizedPoints({ ...(prev || {}), ...(data || {}) }));

        // merge das dimensões sem apagar o que já tínhamos do prefill
        const apiDims = extractDims(data || {});
        setDims(prev => ({
          length: prev.length ?? apiDims.length,
          height: prev.height ?? apiDims.height,
          width:  prev.width  ?? apiDims.width
        }));
      } catch (err) {
        if (cancelled) return;
        console.error("❌ Error fetching load details:", err);
        setError("Failed to load details.");
      }
    })();
    return () => { cancelled = true; };
  }, [loadId]);

  // 4) Fallback sem “Loading…”
  const safeLoad = useMemo(() => {
    if (load) return withNormalizedPoints(load);
    return withNormalizedPoints({
      id: loadId,
      pickupPoints: [],
      deliveryPoints: [],
      clientType: "",
      industryClientName: "",
      carrierClientName: "",
      carrierName: "",
      pickupDeliveries: [],
    });
  }, [load, loadId]);

  // 💰 derive displayable price
  const { price: loadPrice, priceCurrency: loadCurrency } = useMemo(() => extractPrice(safeLoad), [safeLoad]);
  const priceDisplay = useMemo(() => {
    if (loadPrice == null || loadPrice === "") return "";
    return formatMoney(loadPrice, loadCurrency || "EUR");
  }, [loadPrice, loadCurrency]);

  // 🆕 derive displayable notes for driver
  const notes4driverDisplay = useMemo(() => {
    const v = extractNotes4Driver(safeLoad);
    return typeof v === "string" ? v.trim() : "";
  }, [safeLoad]);

  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="load-details-container">
      <div className="load-details-back-arrow" onClick={() => navigate("/loads")}>
        ← Back
      </div>

      <div className="load-details-title-row">
        <h1 className="load-details-title">Load Details</h1>
      </div>

      <div className="load-details-card">
        <form className="load-details-form">
          {/* Header numa só linha: pares à esquerda, badge ao centro, spacer à direita */}
          <div className="ld-header-row">
            {/* pares "Transport Session / Load" */}
            <div className="ld-kv-row">
              <div
                className="ld-kv-pair"
                title={
                  (safeLoad?.transportsessionId
                    ? `Transport Session ID: ${safeLoad.transportsessionId}`
                    : "") || ""
                }
              >
                <span className="ld-kv__key">Transport Session:</span>
                <span className="ld-kv__value">
                  {valueOrEmpty(getSessionCounter(safeLoad)) || "N/A"}
                </span>
              </div>
              <span className="ld-kv__sep">•</span>
              <div
                className="ld-kv-pair"
                title={
                  (safeLoad?.id ? `Load ID: ${safeLoad.id}` : "") || ""
                }
              >
                <span className="ld-kv__key">Load:</span>
                <span className="ld-kv__value">
                  {valueOrEmpty(
                    safeLoad?.loadNumber ?? safeLoad?.uiCounter ?? safeLoad?.id
                  ) || "N/A"}
                </span>
              </div>
            </div>
            {/* badge ao centro */}
            <div className="ld-badge">
              {safeLoad?.loadBearer && (
                <div className="load-details-type-label load-details-bearer">
                  LoadBearer
                </div>
              )}
              {safeLoad?.superImposable && (
                <div className="load-details-type-label load-details-superimposable">
                  SuperImposable Load
                </div>
              )}
              {safeLoad?.specialLoad && (
                <div className="load-details-type-label load-details-special">
                  Special Load
                </div>             
              )}
            </div>
            {/* spacer à direita para centrar o badge */}
            <div className="ld-spacer" />
          </div>

          <div className="load-details-columns">
            {/* Column 1: Pickups */}
            <div className="load-details-column">
              <h3 className="load-details-column-title">Loading Points</h3>
              <label className="load-details-label">Pick-Up Place</label>

              {Array.isArray(safeLoad?.pickupPoints) && safeLoad.pickupPoints.length > 0 ? (
                safeLoad.pickupPoints.map((point, index) => (
                  <div key={index} className="load-details-location-row">
                    <div className="load-details-input ld-input-display" title={valueOrEmpty(point?.location)}>
                      <div className="ld-location-main">{valueOrEmpty(point?.location)}</div>
                      {(point?.city || point?.country) && (
                        <div className="ld-location-sub">
                          {valueOrEmpty(point?.city)}
                          {point?.city && point?.country ? ", " : ""}
                          {valueOrEmpty(point?.country)}
                        </div>
                      )}
                    </div>
                    <div className="load-details-datetime-group">
                      <div className="load-details-icon-input">
                        <FaCalendarAlt className="load-details-icon" />
                        <input
                          type="text"
                          value={formatDisplayDate(point?.date)}
                          readOnly
                          className="load-details-input with-icon"
                        />
                      </div>
                      <div className="load-details-icon-input">
                        <FaClock className="load-details-icon" />
                        <input
                          type="text"
                          value={valueOrEmpty(point?.hour)}
                          readOnly
                          className="load-details-input with-icon"
                        />
                      </div>
                    </div>

                    {showMorePickupFields[index] ? (
                      <>
                        <div className="load-details-extra-fields">
                          {/* Working hours badge */}
                          {(() => {
                            const dayKey = weekdayKeyFromISO(point?.date);
                            const whObj = parseWorkingHours(point?.workingHours ?? point?.place?.workingHours);
                            const text = formatIntervalsForDay(whObj, dayKey);
                            return (
                              <div className="load-details-working-hours-badge">
                                <span className="load-details-working-hours-day">{dayKey ?? "—"}</span>
                                <span className="load-details-working-hours-text">
                                  {text ?? "No working hours info"}
                                </span>
                              </div>
                            );
                          })()}
                          <input type="text" value={valueOrEmpty(point?.address)} readOnly className="load-details-input" placeholder="Address" />
                          <div className="load-details-row-group">
                            <input type="text" value={valueOrEmpty(point?.postalCode)} readOnly className="load-details-input small" placeholder="Postal Code" />
                            <input type="text" value={valueOrEmpty(point?.city)} readOnly className="load-details-input small" placeholder="City" />
                            <input type="text" value={valueOrEmpty(point?.country)} readOnly className="load-details-input small" placeholder="Country" />
                          </div>
                          <input type="text" value={valueOrEmpty(point?.gMapsLink)} readOnly className="load-details-input" placeholder="Google Maps Link" />
                          <input type="text" value={valueOrEmpty(point?.coordinates)} readOnly className="load-details-input" placeholder="Coordinates" />
                          <input type="text" value={valueOrEmpty(point?.contactPhone)} readOnly className="load-details-input" placeholder="Contact Phone" />
                        </div>

                        <div className="edit-special-load-toggle-fields-wrapper">
                          <button
                            type="button"
                            className="edit-special-load-toggle-fields-btn"
                            onClick={() => setShowMorePickupFields((prev) => ({ ...prev, [index]: false }))}
                          >
                            Hide fields <FiChevronUp className="edit-special-load-toggle-arrow" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="edit-special-load-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-special-load-toggle-fields-btn"
                          onClick={() => setShowMorePickupFields((prev) => ({ ...prev, [index]: true }))}
                        >
                          + More fields <FiChevronDown className="edit-special-load-toggle-arrow" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="load-details-empty">No Pick-Up points defined.</p>
              )}
            </div>

            {/* Column 2: Merchandise + Client */}
            <div className="load-details-column">
              <h3 className="load-details-column-title">Merchandise</h3>
              <div className="load-details-merchandise-row">
                <div className="load-details-field">
                  <label className="load-details-label">Length (m)</label>
                  <input
                    type="text"
                    value={valueOrEmpty(dims.length)}
                    readOnly
                    className="load-details-input"
                  />
                </div>
                <div className="load-details-field">
                  <label className="load-details-label">Height (m)</label>
                  <input
                    type="text"
                    value={valueOrEmpty(dims.height)}
                    readOnly
                    className="load-details-input"
                  />
                </div>
                <div className="load-details-field">
                  <label className="load-details-label">Width (m)</label>
                  <input
                    type="text"
                    value={valueOrEmpty(dims.width)}
                    readOnly
                    className="load-details-input"
                  />
                </div>
              </div>

              <h3 className="load-details-column-title">General Information</h3>

              {/* 💰 Price */}
              <div className="load-details-field">
                <label className="load-details-label">Price</label>
                <input
                  type="text"
                  readOnly
                  value={priceDisplay || "—"}
                  className="load-details-input"
                  title={priceDisplay || ""}
                />
              </div>

              {/* 🆕 Notes for Driver */}
              <div className="load-details-field">
                <label className="load-details-label">Notes for Driver</label>
                <textarea
                  readOnly
                  rows={3}
                  value={notes4driverDisplay || "—"}
                  className="load-details-input"
                  style={{ resize: "vertical", minHeight: 72 }}
                  title={notes4driverDisplay || ""}
                />
              </div>

              {/* (existing Client Type remains as-is) */}
              <div className="load-details-field">
                <label className="load-details-label">Client Type</label>
                <input
                  type="text"
                  readOnly
                  value={valueOrEmpty(computeClientType(safeLoad)) || "N/A"}
                  className="load-details-input"
                />
              </div>

              <div className="load-details-field">
                <label className="load-details-label">
                  {safeLoad?.industryClient
                    ? "Industry Client"
                    : safeLoad?.carrierClient
                    ? "Carrier Client"
                    : "Client"}
                </label>
                <input
                  type="text"
                  readOnly
                  value={valueOrEmpty(safeLoad?.industryClientName || safeLoad?.carrierClientName) || "N/A"}
                  className="load-details-input"
                />
              </div>

              <div className="load-details-field">
                <label className="load-details-label">Transport Carrier</label>
                <input type="text" readOnly value={valueOrEmpty(safeLoad?.carrierName) || "N/A"} className="load-details-input" />
              </div>
            </div>

            {/* Column 3: Deliveries */}
            <div className="load-details-column">
              <h3 className="load-details-column-title">Unloading Points</h3>
              <label className="load-details-label">Delivery Place</label>

              {Array.isArray(safeLoad?.deliveryPoints) && safeLoad.deliveryPoints.length > 0 ? (
                safeLoad.deliveryPoints.map((point, index) => (
                  <div key={index} className="load-details-location-row">
                    <div className="load-details-input ld-input-display" title={valueOrEmpty(point?.location)}>
                      <div className="ld-location-main">{valueOrEmpty(point?.location)}</div>
                      {(point?.city || point?.country) && (
                        <div className="ld-location-sub">
                          {valueOrEmpty(point?.city)}
                          {point?.city && point?.country ? ", " : ""}
                          {valueOrEmpty(point?.country)}
                        </div>
                      )}
                    </div>

                    <div className="load-details-datetime-group">
                      <div className="load-details-icon-input">
                        <input type="text" value={formatDisplayDate(point?.date)} readOnly className="load-details-input with-icon" placeholder="Date" />
                        <FaCalendarAlt className="load-details-icon" />
                      </div>
                      <div className="load-details-icon-input">
                        <input type="text" value={valueOrEmpty(point?.hour)} readOnly className="load-details-input with-icon" placeholder="Hour" />
                        <FaClock className="load-details-icon" />
                      </div>
                    </div>

                    {showMoreDeliveryFields[index] ? (
                      <>
                        <div className="load-details-extra-fields">
                          {/* Working hours badge */}
                          {(() => {
                            const dayKey = weekdayKeyFromISO(point?.date);
                            const whObj = parseWorkingHours(point?.workingHours ?? point?.place?.workingHours);
                            const text = formatIntervalsForDay(whObj, dayKey);
                            return (
                              <div className="load-details-working-hours-badge">
                                <span className="load-details-working-hours-day">{dayKey ?? "—"}</span>
                                <span className="load-details-working-hours-text">
                                  {text ?? "No working hours info"}
                                </span>
                              </div>
                            );
                          })()}
                          <input type="text" value={valueOrEmpty(point?.address)} readOnly className="load-details-input" placeholder="Address" />
                          <div className="load-details-row-group">
                            <input type="text" value={valueOrEmpty(point?.postalCode)} readOnly className="load-details-input" placeholder="Postal Code" />
                            <input type="text" value={valueOrEmpty(point?.city)} readOnly className="load-details-input" placeholder="City" />
                            <input type="text" value={valueOrEmpty(point?.country)} readOnly className="load-details-input" placeholder="Country" />
                          </div>
                            <input type="text" value={valueOrEmpty(point?.gMapsLink)} readOnly className="load-details-input" placeholder="Google Maps Link" />
                            <input type="text" value={valueOrEmpty(point?.coordinates)} readOnly className="load-details-input" placeholder="Coordinates" />
                            <input type="text" value={valueOrEmpty(point?.contactPhone)} readOnly className="load-details-input" placeholder="Contact Phone" />
                        </div>

                        <div className="edit-special-load-toggle-fields-wrapper">
                          <button
                            type="button"
                            className="edit-special-load-toggle-fields-btn"
                            onClick={() => setShowMoreDeliveryFields((prev) => ({ ...prev, [index]: false }))}
                          >
                            Hide fields <FiChevronUp className="edit-special-load-toggle-arrow" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="edit-special-load-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-special-load-toggle-fields-btn"
                          onClick={() => setShowMoreDeliveryFields((prev) => ({ ...prev, [index]: true }))}
                        >
                          + More fields <FiChevronDown className="edit-special-load-toggle-arrow" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="load-details-empty">No Delivery points defined.</p>
              )}
            </div>
          </div>

          {/* Edit Button */}
          <div className="load-details-button-container">
            <button
              type="button"
              className="load-details-edit-button"
              onClick={() => {
                // garante que o editor recebe SEMPRE carrierId + carrierName
                const resolvedCarrierId =
                  safeLoad?.carrierId ??
                  safeLoad?.loadsAsCarrier?.id ??         // se vier desta associação
                  safeLoad?.carrier?.id ??                // ou desta
                  null;

                const resolvedCarrierName =
                  safeLoad?.carrierName ??
                  safeLoad?.loadsAsCarrier?.companyName ?? 
                  safeLoad?.carrier?.companyName ??
                  null;

                const l = {
                  ...safeLoad,
                  ...dims,                  // mantém dimensões no prefill
                  carrierId: resolvedCarrierId,
                  carrierName: resolvedCarrierName,
                };

                if (!l) return;
                const base = `/loads/${l.id}`;
                const st = { state: { prefill: l } };

                if (l.loadBearer) navigate(`${base}/edit-bearer`, st);
                else if (l.superImposable) navigate(`${base}/edit-superimposable`, st);
                else if (l.specialLoad) navigate(`${base}/edit-special`, st);
                else navigate(`${base}/edit-normal`, st);
              }}
            >
              Edit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
